const TOKEN_KEY = 'erp_token';

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t: string) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

// 运行环境探测：是否处于 Tauri 桌面/移动 App 内（自带后端进程，监听 3001）
const isTauri =
  typeof window !== 'undefined' &&
  ('__TAURI_INTERNALS__' in window || '__TAURI__' in window);

// API 基地址：
// - Tauri App 内 → 直连内置后端 http://localhost:3001
// - 网页/PWA 版 → 相对路径（开发靠 Vite 代理，部署靠同源 nginx）
export const API_BASE = isTauri ? 'http://localhost:3001' : (import.meta.env.VITE_API_BASE || '');

// 统一后端请求封装：自动携带 JWT，401 自动跳登录
export async function api(path: string, options: RequestInit = {}) {
  const headers: any = { 'Content-Type': 'application/json', ...(options.headers || {}) };
  const token = getToken();
  if (token) headers['Authorization'] = 'Bearer ' + token;
  // 10 秒超时：避免后端偶发卡住时前端永久 pending（表现为「假死、刷新才恢复」）
  // 部分旧浏览器/兼容模式不支持 AbortController， fallback 到普通 fetch
  const supportsAbort = typeof AbortController !== 'undefined';
  const controller = supportsAbort ? new AbortController() : null;
  const timer = supportsAbort ? setTimeout(() => controller?.abort(), 10000) : null;
  try {
    const fetchOptions: RequestInit = { ...options, headers };
    if (controller) fetchOptions.signal = controller.signal;
    const res = await fetch(API_BASE + '/api' + path, fetchOptions);
    if (res.status === 401) {
      clearToken();
      if (location.pathname !== '/login') location.href = '/login';
      throw new Error('登录已过期');
    }
    const data = await res.json();
    if (data.code !== 0) throw new Error(data.msg || '请求失败');
    return data.data;
  } catch (e: any) {
    if (e?.name === 'AbortError') throw new Error('请求超时，请刷新重试');
    throw e;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

// 通知接口封装
export const notificationApi = {
  list: (unread?: boolean) => api('/notifications' + (unread ? '?unread=1' : '')),
  count: () => api('/notifications/count'),
  read: (id: number) => api(`/notifications/${id}/read`, { method: 'POST' }),
  readAll: () => api('/notifications/read-all', { method: 'POST' }),
  remove: (id: number) => api(`/notifications/${id}`, { method: 'DELETE' }),
  send: (payload: any) => api('/notifications', { method: 'POST', body: JSON.stringify(payload) }),
};

// ============ 客户自助门户 API（独立 token，与内部系统隔离） ============
const CUST_TOKEN_KEY = 'erp_cust_token';
export const getCustToken = () => localStorage.getItem(CUST_TOKEN_KEY);
export const setCustToken = (t: string) => localStorage.setItem(CUST_TOKEN_KEY, t);
export const clearCustToken = () => localStorage.removeItem(CUST_TOKEN_KEY);

export async function portalApi(path: string, options: RequestInit = {}) {
  const headers: any = { 'Content-Type': 'application/json', ...(options.headers || {}) };
  const token = getCustToken();
  if (token) headers['Authorization'] = 'Bearer ' + token;
  const res = await fetch(API_BASE + '/api/portal' + path, { ...options, headers });
  if (res.status === 401) {
    clearCustToken();
    if (location.pathname !== '/portal/login') location.href = '/portal/login';
    throw new Error('登录已过期');
  }
  const data = await res.json();
  if (data.code !== 0) throw new Error(data.msg || '请求失败');
  return data.data;
}
